import { Typography } from '@mui/material';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import type { SxProps, Theme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import { useSidebar } from '@src/context/hooks';
import type { ElementType, ReactNode } from 'react';

interface SlotSx {
  sx?: SxProps<Theme>;
}

interface SidebarItemProps {
  label?: ReactNode;
  icon?: ReactNode;
  component?: ElementType;
  slotProps?: {
    listItem?: SlotSx;
    listItemButton?: SlotSx;
  };
  [key: string]: unknown;
}

function SidebarItem(props: SidebarItemProps) {
  const { label, icon, component, slotProps, ...restProps } = props;
  const {
    listItem: listItemProps = {},
    listItemButton: listItemButtonProps = {},
  } = slotProps || {};

  const sidebar = useSidebar();
  if (!sidebar) {
    return null;
  }
  const { isCollapsed } = sidebar;

  const content = (
    <ListItem
      disablePadding
      sx={[StyleSheet.listItem, listItemProps.sx ?? false]}
    >
      <ListItemButton
        sx={
          [
            StyleSheet.listItemButton,
            listItemButtonProps.sx ?? false,
          ] as SxProps<Theme>
        }
        {...(component ? { component } : {})}
        {...restProps}
      >
        <ListItemIcon sx={StyleSheet.listItemIcon}>{icon}</ListItemIcon>

        {!isCollapsed ? (
          <ListItemText
            primary={
              <Typography
                variant="h5"
                sx={StyleSheet.listItemText}
                color="inherit"
              >
                {label}
              </Typography>
            }
          />
        ) : null}
      </ListItemButton>
    </ListItem>
  );

  if (isCollapsed) {
    return (
      <Tooltip arrow placement="right" title={label}>
        {content}
      </Tooltip>
    );
  }

  return content;
}

const StyleSheet = {
  listItem: {},
  listItemButton: {
    height: '48px',
    boxSizing: 'border-box',
    zIndex: 1201,
    mb: 0.5,
  },
  listItemIcon: (theme: Theme) => ({
    minWidth: '24px',
    mr: 2,
    stroke: theme.palette.common.white,
    '& .MuiSvgIcon-root': {
      height: '20px',
      width: '20px',
    },
  }),
  listItemText: (theme: Theme) => ({
    color: theme.palette.grey[300],
  }),
};

SidebarItem.displayName = 'SidebarItem';
export default SidebarItem;
